import { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import {
  MOOD_CODES,
  MOOD_META,
  moodToEmoji,
  resolveScore,
  resolveLabel,
  lastTwoSentences,
} from '../../utils/moodScale';
import { isEmotion, EMOTION_META } from '../../utils/emotions';

const SAGE = '#7C9A82';
// Warm mid-stone — dark enough to read against sage area fill on mobile but
// still visually secondary to the solid After-Echo line.
const BEFORE_STROKE = '#8A8580';

// Two chart layouts, picked at runtime by a matchMedia hook. The mobile layout
// uses a near-portrait viewBox so the plot area doesn't collapse to a squished
// band on a 375px-wide phone; desktop keeps the wide landscape shape that
// matches the KPI row above. Every measurement — viewBox, padding, font sizes,
// marker radii — lives inside a layout so nothing forgets to switch. Y ticks
// are the mood ladder (rough → great).
const LAYOUTS = {
  mobile: {
    w: 400,
    h: 460,
    pad: { top: 24, right: 20, bottom: 44, left: 52 },
    font: {
      yEmoji: 20,       // larger since the viewBox is smaller — renders ~17px
      xTick: 14,
      markerBefore: 15,
      markerAfter: 17,
    },
    marker: { dotR: 3, activeR: 7, activeHaloR: 12 },
    xTickCount: 4,       // fewer date labels so they don't cram on a phone
  },
  desktop: {
    w: 720,
    h: 380,
    pad: { top: 28, right: 32, bottom: 52, left: 60 },
    font: {
      yEmoji: 16,
      xTick: 11,
      markerBefore: 12,
      markerAfter: 13,
    },
    marker: { dotR: 2.5, activeR: 6, activeHaloR: 10 },
    xTickCount: 5,
  },
};

const Y_MIN = 0;
const Y_MAX = 10;
const Y_TICK_CODES = MOOD_CODES;

// Legacy mood → representative emotion pastel. Pre-v2 rows carry mood_before/
// mood_after but no emotion code, so we tint their dots with a color drawn
// from the matching valence band. Keeps the palette coherent across the
// migration window without introducing a second color system.
const MOOD_DOT_BG = {
  great:   EMOTION_META.happy.bg,
  good:    EMOTION_META.content.bg,
  neutral: EMOTION_META.numb.bg,
  low:     EMOTION_META.sad.bg,
  rough:   EMOTION_META.overwhelmed.bg,
};

const NEUTRAL_DOT = 'rgba(120, 113, 108, 0.35)';

function dotFillFor(emotionCode, moodCode, isPost) {
  if (emotionCode && EMOTION_META[emotionCode]) return EMOTION_META[emotionCode].bg;
  if (moodCode && MOOD_DOT_BG[moodCode]) return MOOD_DOT_BG[moodCode];
  // Nothing labelled → keep the visual weight on the After track (solid sage)
  // and let the Before track fade back so the dashed line reads as secondary.
  return isPost ? SAGE : NEUTRAL_DOT;
}

// Monotone-ish cubic smoothing that produces a horizontal S-curve between each
// pair of points. Cheaper than a real monotone-cubic (no derivative pass) and
// visually indistinguishable for a five-point ordinal series. Straight lines
// look austere; this curve reads as "gentle drift".
function smoothPath(points) {
  if (points.length === 0) return '';
  if (points.length === 1) {
    const [x, y] = points[0];
    return `M ${x},${y}`;
  }
  let d = `M ${points[0][0]},${points[0][1]}`;
  for (let i = 1; i < points.length; i++) {
    const [x0, y0] = points[i - 1];
    const [x1, y1] = points[i];
    const midX = (x0 + x1) / 2;
    d += ` C ${midX},${y0} ${midX},${y1} ${x1},${y1}`;
  }
  return d;
}

function formatDuration(ms) {
  if (!Number.isFinite(ms) || ms <= 0) return '0:00';
  const secs = Math.round(ms / 1000);
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

const MONTH_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];
const MONTH_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const formatTickDate = (d) => `${MONTH_SHORT[d.getMonth()]} ${d.getDate()}`;
const formatLongDate = (d) =>
  `${MONTH_LONG[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;

function buildPoints(echoes) {
  if (!echoes || echoes.length === 0) {
    return { points: [], domain: null };
  }
  const withTs = echoes
    .map((e) => ({ ...e, _ts: new Date(e.created_at).getTime() }))
    .filter((e) => Number.isFinite(e._ts))
    .sort((a, b) => a._ts - b._ts);
  if (withTs.length === 0) return { points: [], domain: null };
  const minTs = withTs[0]._ts;
  const maxTs = withTs[withTs.length - 1]._ts;
  const span = Math.max(1, maxTs - minTs);
  const points = withTs.map((e) => {
    // v2 emotion (if present) wins over the legacy 5-code mood. Pre-v2 rows
    // fall back to the mood scale untouched, so the trend line remains
    // continuous across the upgrade date.
    const preScore = resolveScore(e.emotion_before, e.mood_before);
    const postScore = resolveScore(e.emotion_after, e.mood_after);
    // An unlabelled side has no score (resolveScore returns null since
    // 2026-09-23). EchoesDashboard filters those out before we ever see them;
    // this is the second line of defence, because the failure mode if one slips
    // through is not a crash but a point silently drawn at the bottom of the
    // scale, which would read as "they felt terrible".
    if (preScore === null || postScore === null) return null;
    const emotionBefore = isEmotion(e.emotion_before) ? e.emotion_before : null;
    const emotionAfter = isEmotion(e.emotion_after) ? e.emotion_after : null;
    const summarySource =
      e.user_edited_text || e.accepted_text || e.raw_transcript || '';
    return {
      id: e.id,
      ts: e._ts,
      date: new Date(e._ts),
      xPct: (e._ts - minTs) / span,
      preScore,
      postScore,
      shift: Math.round((postScore - preScore) * 10) / 10,
      moodBefore: e.mood_before,
      moodAfter: e.mood_after,
      emotionBefore,
      emotionAfter,
      // Legacy emoji glyph (kept for pre-v2 rows). For v2 rows we render the
      // emotion label chip on the chart instead — see the marker block below.
      emojiBefore: emotionBefore ? null : moodToEmoji(e.mood_before),
      emojiAfter: emotionAfter ? null : moodToEmoji(e.mood_after),
      labelBefore: resolveLabel(e.emotion_before, e.mood_before),
      labelAfter: resolveLabel(e.emotion_after, e.mood_after),
      durationMs: e.duration_ms || 0,
      summary: lastTwoSentences(summarySource),
    };
  }).filter(Boolean);
  return { points, domain: { minTs, maxTs } };
}

// Even X-axis ticks: N labels spaced across the domain (N comes from the
// active layout).
function buildXTicks(domain, count) {
  if (!domain) return [];
  const { minTs, maxTs } = domain;
  const step = (maxTs - minTs) / Math.max(1, count - 1);
  return Array.from({ length: count }, (_, i) => {
    const ts = minTs + step * i;
    return { pct: (ts - minTs) / Math.max(1, maxTs - minTs), date: new Date(ts) };
  });
}

// Reactive breakpoint. Server render / test defaults to desktop (matches=false)
// which keeps existing behavior for any consumer that didn't wire matchMedia.
// Defensive around jsdom quirks: some environments return undefined from
// matchMedia even after we've mocked it; we treat any non-object return as
// "not mobile" rather than crashing the render.
function useIsMobile(query = '(max-width: 639px)') {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    let mql;
    try {
      mql = window.matchMedia(query);
    } catch (_e) {
      return undefined;
    }
    if (!mql || typeof mql.matches !== 'boolean') return undefined;
    const update = () => setIsMobile(mql.matches);
    update();
    if (mql.addEventListener) {
      mql.addEventListener('change', update);
      return () => mql.removeEventListener('change', update);
    }
    if (mql.addListener) {
      mql.addListener(update);
      return () => mql.removeListener(update);
    }
    return undefined;
  }, [query]);
  return isMobile;
}

function Placeholder({ children }) {
  return (
    <div
      className="rounded-xl bg-stone-50 dark:bg-stone-800/30 border border-dashed border-stone-200 dark:border-stone-700/60 px-6 py-10 text-center"
    >
      <div className="text-3xl mb-2" aria-hidden="true">🌱</div>
      <p className="text-sm text-stone-600 dark:text-stone-300 leading-relaxed max-w-md mx-auto">
        {children}
      </p>
    </div>
  );
}

export default function EmotionalResonanceChart({ echoes }) {
  const wrapperRef = useRef(null);
  const [hoverIdx, setHoverIdx] = useState(null);
  const isMobile = useIsMobile();
  const L = isMobile ? LAYOUTS.mobile : LAYOUTS.desktop;
  const plotW = L.w - L.pad.left - L.pad.right;
  const plotH = L.h - L.pad.top - L.pad.bottom;

  const scaleX = useCallback(
    (pct) => L.pad.left + pct * plotW,
    [L.pad.left, plotW]
  );
  const scaleY = useCallback(
    (v) => L.pad.top + (1 - (v - Y_MIN) / (Y_MAX - Y_MIN)) * plotH,
    [L.pad.top, plotH]
  );

  const { points, domain } = useMemo(() => buildPoints(echoes), [echoes]);
  const xTicks = useMemo(
    () => buildXTicks(domain, L.xTickCount),
    [domain, L.xTickCount]
  );

  const preCoords = useMemo(
    () => points.map((p) => [scaleX(p.xPct), scaleY(p.preScore)]),
    [points, scaleX, scaleY]
  );
  const postCoords = useMemo(
    () => points.map((p) => [scaleX(p.xPct), scaleY(p.postScore)]),
    [points, scaleX, scaleY]
  );
  const prePath = useMemo(() => smoothPath(preCoords), [preCoords]);
  const postPath = useMemo(() => smoothPath(postCoords), [postCoords]);
  const postAreaPath = useMemo(() => {
    if (postCoords.length === 0) return '';
    const base = smoothPath(postCoords);
    const last = postCoords[postCoords.length - 1];
    const first = postCoords[0];
    return `${base} L ${last[0]},${L.pad.top + plotH} L ${first[0]},${L.pad.top + plotH} Z`;
  }, [postCoords, L.pad.top, plotH]);

  const handleMove = useCallback(
    (evt) => {
      if (!wrapperRef.current || points.length === 0) return;
      const svg = wrapperRef.current.querySelector('svg');
      if (!svg) return;
      const rect = svg.getBoundingClientRect();
      const clientX = evt.clientX ?? evt.touches?.[0]?.clientX;
      if (clientX == null) return;
      const relX = clientX - rect.left;
      const svgX = (relX / rect.width) * L.w;
      let bestIdx = 0;
      let bestDist = Infinity;
      for (let i = 0; i < points.length; i++) {
        const px = scaleX(points[i].xPct);
        const dist = Math.abs(px - svgX);
        if (dist < bestDist) {
          bestDist = dist;
          bestIdx = i;
        }
      }
      setHoverIdx(bestIdx);
    },
    [points, scaleX, L.w]
  );

  const handleLeave = useCallback(() => setHoverIdx(null), []);

  const activePoint = hoverIdx != null ? points[hoverIdx] : null;
  const activeAfterX = activePoint ? scaleX(activePoint.xPct) : 0;
  const activeAfterY = activePoint ? scaleY(activePoint.postScore) : 0;

  const tooltipLeftPct = activePoint ? (activeAfterX / L.w) * 100 : 0;
  const tooltipTopPct = activePoint ? (activeAfterY / L.h) * 100 : 0;
  const flipTop = tooltipTopPct < 30;

  const hasEnoughData = points.length >= 3;

  return (
    <section
      data-testid="resonance-chart"
      className="rounded-2xl sm:rounded-3xl bg-white dark:bg-stone-800/40 border border-stone-200/60 dark:border-stone-700/40 shadow-sm p-4 sm:p-8"
    >
      <header className="mb-3 sm:mb-6">
        <h3 className="text-base sm:text-xl font-semibold tracking-tight text-stone-800 dark:text-[#F0EDE8]">
          Emotional Resonance Trend
        </h3>
        <p className="mt-1 text-xs sm:text-sm text-stone-600 dark:text-stone-300 leading-relaxed">
          Tracking how your 60-second reflections shift your baseline sentiment.
        </p>
      </header>

      {!hasEnoughData && (
        <Placeholder>
          A few more reflections and your trend will bloom here. Each Echo adds
          a new point to your resonance chart.
        </Placeholder>
      )}

      {hasEnoughData && (
        <div
          ref={wrapperRef}
          className="relative select-none"
          onMouseMove={handleMove}
          onMouseLeave={handleLeave}
          onTouchStart={handleMove}
          onTouchMove={handleMove}
          onTouchEnd={handleLeave}
        >
          <svg
            viewBox={`0 0 ${L.w} ${L.h}`}
            preserveAspectRatio="none"
            role="img"
            aria-label="Line chart showing pre-recording and post-recording mood over time"
            className="w-full h-auto"
            style={{ display: 'block' }}
          >
            <defs>
              <linearGradient id="postAreaGradient" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor={SAGE} stopOpacity="0.28" />
                <stop offset="100%" stopColor={SAGE} stopOpacity="0" />
              </linearGradient>
            </defs>

            {/* Horizontal gridlines — mood emojis are the Y-axis labels */}
            {Y_TICK_CODES.map((code) => {
              const meta = MOOD_META[code];
              const y = scaleY(meta.score);
              return (
                <g key={`grid-${code}`}>
                  <line
                    x1={L.pad.left}
                    x2={L.pad.left + plotW}
                    y1={y}
                    y2={y}
                    stroke="currentColor"
                    strokeOpacity="0.1"
                    strokeDasharray="2 4"
                    className="text-stone-400"
                  />
                  <text
                    x={L.pad.left - (isMobile ? 24 : 22)}
                    y={y + 6}
                    textAnchor="middle"
                    fontSize={L.font.yEmoji}
                    aria-label={meta.label}
                    style={{ pointerEvents: 'none' }}
                  >
                    {meta.emoji}
                  </text>
                </g>
              );
            })}

            {/* Filled sage area under the After-Echo line */}
            {postAreaPath && (
              <path d={postAreaPath} fill="url(#postAreaGradient)" />
            )}

            {/* Before Recording (dashed) */}
            {prePath && (
              <path
                d={prePath}
                fill="none"
                stroke={BEFORE_STROKE}
                strokeWidth={isMobile ? 2.5 : 2}
                strokeDasharray="5 5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}

            {/* After Echo (solid sage) */}
            {postPath && (
              <path
                d={postPath}
                fill="none"
                stroke={SAGE}
                strokeWidth={isMobile ? 3 : 2.5}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}

            {/* Layer A: colored dots at every point. Color = emotion pastel
                (v2 rows) or a mood-tinted pastel (pre-v2 rows) so the density
                + valence of the run reads at a glance without labels. */}
            {points.map((p, i) => {
              const [preX, preY] = preCoords[i];
              const [postX, postY] = postCoords[i];
              const preFill = dotFillFor(p.emotionBefore, p.moodBefore, false);
              const postFill = dotFillFor(p.emotionAfter, p.moodAfter, true);
              return (
                <g key={`dot-${p.id}`} style={{ pointerEvents: 'none' }}>
                  <circle
                    cx={preX}
                    cy={preY}
                    r={L.marker.dotR + 0.5}
                    fill={preFill}
                    stroke="rgba(255,255,255,0.9)"
                    strokeWidth={0.6}
                  />
                  <circle
                    cx={postX}
                    cy={postY}
                    r={L.marker.dotR + 1}
                    fill={postFill}
                    stroke="rgba(255,255,255,0.9)"
                    strokeWidth={0.6}
                  />
                </g>
              );
            })}

            {/* Layer B: pill labels. Sparse — a pill is only drawn if it
                won't overlap the previous pill on its own track. Endpoints
                (first + last point) are forced so the timeline anchors read
                even if the middle is dense. Rendered AFTER dots + paths so
                nothing paints over the label text. */}
            {(() => {
              const pillFont = isMobile ? 10 : 9;
              const pillPadX = isMobile ? 5 : 4;
              const pillPadY = isMobile ? 3 : 2;
              const pillH = pillFont + pillPadY * 2;
              const gapPx = isMobile ? 6 : 8;
              const preLabelDy = isMobile ? 14 : 12;
              const postLabelDy = isMobile ? 14 : 12;
              const lastIdx = points.length - 1;
              let lastPreRight = -Infinity;
              let lastPostRight = -Infinity;
              return points.map((p, i) => {
                const preMeta = p.emotionBefore ? EMOTION_META[p.emotionBefore] : null;
                const postMeta = p.emotionAfter ? EMOTION_META[p.emotionAfter] : null;
                if (!preMeta && !postMeta) return null;
                const [preX, preY] = preCoords[i];
                const [postX, postY] = postCoords[i];
                const force = i === 0 || i === lastIdx;

                let showPre = false;
                let preW = 0;
                if (preMeta) {
                  preW = preMeta.label.length * pillFont * 0.62 + pillPadX * 2;
                  const left = preX - preW / 2;
                  if (force || left >= lastPreRight + gapPx) {
                    showPre = true;
                    lastPreRight = preX + preW / 2;
                  }
                }
                let showPost = false;
                let postW = 0;
                if (postMeta) {
                  postW = postMeta.label.length * pillFont * 0.62 + pillPadX * 2;
                  const left = postX - postW / 2;
                  if (force || left >= lastPostRight + gapPx) {
                    showPost = true;
                    lastPostRight = postX + postW / 2;
                  }
                }

                if (!showPre && !showPost) return null;
                const preLabelY = preY + preLabelDy;
                const postLabelY = postY - postLabelDy;

                return (
                  <g key={`pill-${p.id}`} style={{ pointerEvents: 'none' }}>
                    {showPre && (
                      <g data-testid="marker-before-pill">
                        <rect
                          x={preX - preW / 2}
                          y={preLabelY - pillH / 2}
                          width={preW}
                          height={pillH}
                          rx={pillH / 2}
                          fill={preMeta.bg}
                        />
                        <text
                          x={preX}
                          y={preLabelY + pillFont * 0.35}
                          textAnchor="middle"
                          fontSize={pillFont}
                          fontWeight="600"
                          fill={preMeta.fg}
                        >
                          {preMeta.label}
                        </text>
                      </g>
                    )}
                    {showPost && (
                      <g data-testid="marker-after-pill">
                        <rect
                          x={postX - postW / 2}
                          y={postLabelY - pillH / 2}
                          width={postW}
                          height={pillH}
                          rx={pillH / 2}
                          fill={postMeta.bg}
                        />
                        <text
                          x={postX}
                          y={postLabelY + pillFont * 0.35}
                          textAnchor="middle"
                          fontSize={pillFont}
                          fontWeight="600"
                          fill={postMeta.fg}
                        >
                          {postMeta.label}
                        </text>
                      </g>
                    )}
                  </g>
                );
              });
            })()}

            {/* Active point highlight */}
            {activePoint && (
              <g>
                <line
                  x1={activeAfterX}
                  x2={activeAfterX}
                  y1={L.pad.top}
                  y2={L.pad.top + plotH}
                  stroke={SAGE}
                  strokeOpacity="0.35"
                  strokeWidth="1"
                  strokeDasharray="3 3"
                />
                <circle
                  cx={activeAfterX}
                  cy={activeAfterY}
                  r={L.marker.activeR}
                  fill={SAGE}
                />
                <circle
                  cx={activeAfterX}
                  cy={activeAfterY}
                  r={L.marker.activeHaloR}
                  fill={SAGE}
                  fillOpacity="0.15"
                />
              </g>
            )}

            {/* X-axis date ticks */}
            {xTicks.map((tick, i) => (
              <text
                key={`xtick-${i}`}
                x={scaleX(tick.pct)}
                y={L.pad.top + plotH + (isMobile ? 26 : 22)}
                textAnchor="middle"
                fontSize={L.font.xTick}
                className="fill-stone-500 dark:fill-stone-400"
              >
                {formatTickDate(tick.date)}
              </text>
            ))}
          </svg>

          {/* HTML tooltip — positioned as % over the SVG box */}
          {activePoint && (
            <div
              data-testid="chart-tooltip"
              className="pointer-events-none absolute z-10 w-60 sm:w-72 -translate-x-1/2 rounded-2xl bg-white dark:bg-stone-900 shadow-xl border border-stone-200/70 dark:border-stone-700/60 p-3 sm:p-4"
              style={{
                left: `${tooltipLeftPct}%`,
                top: flipTop ? `calc(${tooltipTopPct}% + 24px)` : undefined,
                bottom: flipTop
                  ? undefined
                  : `calc(${100 - tooltipTopPct}% + 24px)`,
              }}
            >
              <div className="flex items-center justify-between gap-2 text-xs text-stone-500 dark:text-stone-400">
                <span className="font-semibold text-stone-700 dark:text-stone-200">
                  {formatLongDate(activePoint.date)}
                </span>
                <span className="tabular-nums">
                  {formatDuration(activePoint.durationMs)} audio
                </span>
              </div>

              <div className="mt-3 space-y-2 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    {activePoint.emotionBefore
                      ? (
                        <span
                          className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold"
                          style={{
                            backgroundColor: EMOTION_META[activePoint.emotionBefore].bg,
                            color: EMOTION_META[activePoint.emotionBefore].fg,
                          }}
                        >
                          {activePoint.labelBefore}
                        </span>
                      )
                      : (
                        <>
                          <span aria-hidden="true">{activePoint.emojiBefore}</span>
                          <span className="text-stone-700 dark:text-stone-200 truncate">
                            {activePoint.labelBefore}
                          </span>
                        </>
                      )}
                    <span className="text-[10px] uppercase tracking-[0.14em] font-semibold text-stone-400">
                      Before
                    </span>
                  </div>
                  <span className="tabular-nums text-stone-500 dark:text-stone-400 shrink-0">
                    {activePoint.preScore}/10
                  </span>
                </div>

                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    {activePoint.emotionAfter
                      ? (
                        <span
                          className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold"
                          style={{
                            backgroundColor: EMOTION_META[activePoint.emotionAfter].bg,
                            color: EMOTION_META[activePoint.emotionAfter].fg,
                          }}
                        >
                          {activePoint.labelAfter}
                        </span>
                      )
                      : (
                        <>
                          <span aria-hidden="true">{activePoint.emojiAfter}</span>
                          <span className="text-stone-700 dark:text-stone-200 truncate">
                            {activePoint.labelAfter}
                          </span>
                        </>
                      )}
                    <span className="text-[10px] uppercase tracking-[0.14em] font-semibold text-stone-400">
                      After
                    </span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="tabular-nums text-stone-500 dark:text-stone-400">
                      {activePoint.postScore}/10
                    </span>
                    {activePoint.shift !== 0 && (
                      <span
                        data-testid="tooltip-shift"
                        className="rounded-full px-1.5 py-0.5 text-[10px] font-semibold text-white tabular-nums"
                        style={{
                          backgroundColor:
                            activePoint.shift > 0 ? SAGE : '#D4A574',
                        }}
                      >
                        {activePoint.shift > 0 ? '+' : ''}
                        {activePoint.shift}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {activePoint.summary && (
                <blockquote
                  data-testid="tooltip-summary"
                  className="mt-3 pt-3 border-t border-stone-100 dark:border-stone-700/60 text-xs italic leading-relaxed text-stone-500 dark:text-stone-400"
                  style={{ color: undefined }}
                >
                  “{activePoint.summary}”
                </blockquote>
              )}
            </div>
          )}
        </div>
      )}

      {/* Legend */}
      {hasEnoughData && (
        <div className="mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-stone-600 dark:text-stone-300">
          <span className="inline-flex items-center gap-2">
            <span
              aria-hidden="true"
              className="inline-block h-[2px] w-6"
              style={{
                backgroundImage: `linear-gradient(90deg, ${BEFORE_STROKE} 50%, transparent 0)`,
                backgroundSize: '6px 2px',
              }}
            />
            Before Recording
          </span>
          <span className="inline-flex items-center gap-2">
            <span
              aria-hidden="true"
              className="inline-block h-[3px] w-6 rounded"
              style={{ backgroundColor: SAGE }}
            />
            After Echo
          </span>
        </div>
      )}
    </section>
  );
}

// Exported for tests — mirrors the internal helpers so the surface stays stable.
export { buildPoints, smoothPath, LAYOUTS };
